import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Button from '../shared/TailwindComponents/Button';
import { ColumnDataType } from '../../types/batchTables';

interface AddTableColumnContentProps {
  onConfirm: (data: { name: string; query?: string; data_type: ColumnDataType }) => void;
  onCancel: () => void;
}

const dataTypeOptions: { value: ColumnDataType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'currency', label: 'Currency' },
];

const AddTableColumnContent: React.FC<AddTableColumnContentProps> = ({ onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [dataType, setDataType] = useState<ColumnDataType>('text');

  const handleSubmit = useCallback(() => {
    if (!name.trim()) return;
    onConfirm({
      name: name.trim(),
      query: query.trim() || undefined,
      data_type: dataType,
    });
  }, [name, query, dataType, onConfirm]);

  return (
    <div className="w-[480px]">
      <div className="px-6 py-5 space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('Column Name')} *
          </label>
          <Input
            type="text"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder={t('e.g., Effective Date')}
            autoFocus
          />
        </div>

        {/* Query */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('AI Query')}
            <span className="text-xs text-gray-400 ml-1">({t('defaults to column name if empty')})</span>
          </label>
          <Textarea
            id="column-query"
            label=""
            value={query}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuery(e.target.value)}
            placeholder={t('What is the effective date or execution date of this document?')}
            rows={3}
          />
        </div>

        {/* Data Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('Data Type')}
          </label>
          <div className="flex flex-wrap gap-2">
            {dataTypeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDataType(opt.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                  dataType === opt.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
              >
                {t(opt.label)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="neutral" size="small" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={!name.trim()}
        >
          {t('Add Column')}
        </Button>
      </div>
    </div>
  );
};

export default AddTableColumnContent;
